import React, { useState } from 'react';
import { useStudioStore } from '../../stores/useStudioStore';
import { studioApiService } from '../../services/studioApiService';

export const VarPanelResults: React.FC = () => {
  const { variantQueue, updateVariantProduct, addDbUpload, clearVariantQueue } = useStudioStore();
  const [activeTab, setActiveTab] = useState<'queue' | 'approved' | 'discarded'>('queue');
  const [uploadingVariantId, setUploadingVariantId] = useState<string | null>(null);

  const queue = variantQueue.filter(p => p.reviewState === 'pending');
  const approved = variantQueue.filter(p => p.reviewState === 'approved');
  const discarded = variantQueue.filter(p => p.reviewState === 'discarded');

  const visibleProducts = activeTab === 'queue' ? queue : activeTab === 'approved' ? approved : discarded;

  // Group approved variants by Product ID (variantLabel)
  const approvedGroups = approved.reduce((acc, curr) => {
    if (!acc[curr.variantLabel]) acc[curr.variantLabel] = [];
    acc[curr.variantLabel].push(curr);
    return acc;
  }, {} as Record<string, typeof approved>);

  const handleUploadS3Group = async (productId: string) => {
    const productsInGroup = approvedGroups[productId];
    
    // Extract base64s
    const images = productsInGroup.map(p => p.resultSrc!).filter(Boolean);
    if (images.length === 0) return;

    setUploadingVariantId(productId);
    try {
      const res = await studioApiService.uploadToS3(productId, images);
      if (res.success) {
        addDbUpload({
          rowId: productId,
          imageUrls: res.uploaded,
          status: 'pending'
        });
        alert(`Variant group ${productId} uploaded to S3! Go to DB Upload tab to finish.`);
      }
    } catch (e: any) {
      alert(`S3 Upload failed: ${e.message}`);
    } finally {
      setUploadingVariantId(null);
    }
  };

  return (
    <div className="panel-right">
      <div className="tabs-bar">
        <button className={`tab-btn ${activeTab === 'queue' ? 'active' : ''}`} onClick={() => setActiveTab('queue')}>
          Queue <span className="tab-badge">{queue.length}</span>
        </button>
        <button className={`tab-btn ${activeTab === 'approved' ? 'active' : ''}`} onClick={() => setActiveTab('approved')}>
          Approved <span className="tab-badge">{approved.length}</span>
        </button>
        <button className={`tab-btn ${activeTab === 'discarded' ? 'active' : ''}`} onClick={() => setActiveTab('discarded')}>
          Discarded <span className="tab-badge">{discarded.length}</span>
        </button>
        <div className="tabs-actions">
          <button className="action-btn" onClick={clearVariantQueue}>Clear all</button>
        </div>
      </div>

      <div className="results-area">
        {activeTab === 'approved' && Object.keys(approvedGroups).length > 0 && (
          <div style={{ marginBottom: '20px', padding: '14px', background: 'var(--surface)', borderRadius: '12px', border: '1px solid var(--border)' }}>
            <div className="section-label" style={{ marginBottom: '14px' }}>Ready to Upload</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {Object.entries(approvedGroups).map(([productId, products]) => (
                <div key={productId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px', background: 'var(--surface2)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
                    Variant Group: <strong style={{ color: 'var(--text)' }}>{productId}</strong> ({products.length} images)
                  </div>
                  <button 
                    className="row-act-btn" 
                    style={{ background: 'var(--blue)', color: '#fff' }}
                    onClick={() => handleUploadS3Group(productId)}
                    disabled={uploadingVariantId === productId}
                  >
                    {uploadingVariantId === productId ? 'Uploading...' : `↑ Upload ${productId} to S3`}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {visibleProducts.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">✦</div>
            <div className="empty-title">Nothing here yet</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {visibleProducts.map(p => (
              <div key={p.id} className="result-row">
                <div className="result-row-header">
                  <div className="result-row-id">
                    {p.variantLabel} - {p.angleLabel}
                    <span className={`status-badge ${p.status}`}>{p.status}</span>
                  </div>
                </div>
                <div className="result-row-body" style={{ alignItems: 'center' }}>
                  {p.status === 'generating' ? (
                    <div className="skel-img row-img-full" style={{ width: '150px', height: '150px' }}>
                      <div className="skel-inner">
                        <div className="dots"><span></span><span></span><span></span></div>
                      </div>
                    </div>
                  ) : p.resultSrc ? (
                    <img 
                      src={p.resultSrc.startsWith('data:') || p.resultSrc.startsWith('http') 
                        ? p.resultSrc 
                        : `data:image/png;base64,${p.resultSrc}`
                      } 
                      className="row-img" 
                      style={{ width: '150px', height: '150px' }} 
                      alt="" 
                    />
                  ) : null}
                  
                  <div className="row-actions" style={{ marginLeft: 'auto' }}>
                    {activeTab === 'queue' && p.status === 'complete' && (
                      <>
                        <button className="row-act-btn row-act-approve" onClick={() => updateVariantProduct(p.id, { reviewState: 'approved' })}>✓ Approve</button>
                        <button className="row-act-btn row-act-discard" onClick={() => updateVariantProduct(p.id, { reviewState: 'discarded' })}>✕ Discard</button>
                      </>
                    )}
                    {p.error && <div className="row-error">{p.error}</div>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
